import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { getClassTrends } from '../../services/teacherService';
import { ClassTrend } from '../../types/teacher';

interface ClassAnalyticsCardProps {
  refreshTrigger?: number;
}

const ClassAnalyticsCard: React.FC<ClassAnalyticsCardProps> = ({ refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trends, setTrends] = useState<ClassTrend[]>([]);
  const [timeWindow, setTimeWindow] = useState<number>(12);

  const fetchClassTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClassTrends(timeWindow);
      setTrends(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load class trends';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [timeWindow]);

  useEffect(() => {
    fetchClassTrends();
  }, [refreshTrigger, fetchClassTrends]);

  const handleTimeWindowChange = (event: SelectChangeEvent<number>) => {
    setTimeWindow(Number(event.target.value));
  };

  const getTrendIcon = (trend: 'improving' | 'stable' | 'declining') => {
    switch (trend) {
      case 'improving':
        return <TrendingUpIcon color="success" />;
      case 'declining':
        return <TrendingDownIcon color="error" />;
      case 'stable':
      default:
        return <TrendingFlatIcon color="action" />;
    }
  };

  const getTrendColor = (trend: 'improving' | 'stable' | 'declining') => {
    switch (trend) {
      case 'improving':
        return 'success';
      case 'declining':
        return 'error';
      case 'stable':
      default:
        return 'default';
    }
  };

  const calculateOverallStats = () => {
    if (trends.length === 0) return null;

    const totalSubmissions = trends.reduce((sum, t) => sum + t.totalSubmissions, 0);
    const avgAccuracy =
      trends.reduce((sum, t) => sum + t.avgAccuracy, 0) / trends.length;
    const avgActiveStudents =
      trends.reduce((sum, t) => sum + t.activeStudents, 0) / trends.length;

    // Get most recent week
    const latestTrend = trends[0];

    return {
      totalSubmissions,
      avgAccuracy: Math.round(avgAccuracy),
      avgActiveStudents: Math.round(avgActiveStudents),
      latestTrend
    };
  };

  const stats = calculateOverallStats();

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<AssessmentIcon color="primary" />}
        title="Class Performance Analytics"
        action={
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={timeWindow}
              onChange={handleTimeWindowChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Time window' }}
            >
              <MenuItem value={4}>4 Weeks</MenuItem>
              <MenuItem value={8}>8 Weeks</MenuItem>
              <MenuItem value={12}>12 Weeks</MenuItem>
              <MenuItem value={24}>24 Weeks</MenuItem>
            </Select>
          </FormControl>
        }
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : trends.length === 0 ? (
          <Alert severity="info">No class trend data available yet.</Alert>
        ) : (
          <>
            {/* Overall Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.avgAccuracy}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Accuracy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.totalSubmissions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Submissions
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2} bgcolor="background.default" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats?.avgActiveStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Active Students
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Current Trend */}
            {stats?.latestTrend && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Current Trend
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {getTrendIcon(stats.latestTrend.trend)}
                  <Chip
                    label={stats.latestTrend.trend.toUpperCase()}
                    color={getTrendColor(stats.latestTrend.trend) as 'success' | 'error' | 'default'}
                    size="small"
                  />
                  {stats.latestTrend.percentChange !== 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {stats.latestTrend.percentChange > 0 ? '+' : ''}
                      {Math.round(stats.latestTrend.percentChange)}% from previous week
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Weekly Breakdown */}
            <Typography variant="h6" gutterBottom>
              Weekly Breakdown (Last {timeWindow} Weeks)
            </Typography>
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {trends.map((trend, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" fontWeight="bold">
                        Week {trend.weekNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(trend.weekStartDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {getTrendIcon(trend.trend)}
                        <Typography variant="body2">{trend.avgAccuracy}%</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Accuracy
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Typography variant="body2">{trend.totalSubmissions}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submissions
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <GroupsIcon fontSize="small" color="action" />
                        <Typography variant="body2">{trend.activeStudents}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Active
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {trend.percentChange !== 0 && (
                        <Chip
                          label={`${trend.percentChange > 0 ? '+' : ''}${Math.round(
                            trend.percentChange
                          )}%`}
                          size="small"
                          color={
                            trend.percentChange > 0
                              ? 'success'
                              : trend.percentChange < 0
                              ? 'error'
                              : 'default'
                          }
                          variant="outlined"
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClassAnalyticsCard;
